<script setup lang="ts">
import { computed, type PropType, ref, watch } from 'vue';
import { Icon } from '@iconify/vue';
import Button from 'primevue/button';
import Dialog from 'primevue/dialog';
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import ColumnGroup from 'primevue/columngroup';
import Row from 'primevue/row';
import {formatedDate} from '@/constants'
// @ts-ignore
import html2pdf from 'html2pdf.js';
import type { SalesByCustomerReportResponse } from '@/types/report-types';

const props = defineProps({
  visible: { type: Boolean, required: true },
  onClose: { type: Function as PropType<() => void>, required: true },
  reportData: { type: Object as PropType<SalesByCustomerReportResponse | null>, default: null },
  startDate: { type: Date as PropType<Date | null>, default: null },
  endDate: { type: Date as PropType<Date | null>, default: null },
  customerId: { type: String as PropType<string | null>, default: null },
  companyName: { type: String, default: 'Techknital Fabrics Ltd' },
  companyAddress: { type: String, default: 'Unit 8 - 9, Lime Kilns Business Park\nLime Kilns Way\nHinckley\nLeicestershire, LE10 #EL' },
  companyTel: { type: String, default: '+44 (0)1455 254350' },
  companyFax: { type: String, default: '+44 (0)1455 238998' },
});

const PDF_EXPORT_ELEMENT_ID = 'sales-by-customer-report-content';
const customerNameDisplay = ref<string | null>(null);
const isLoadingCustomerName = ref(false);

const formattedStartDate = computed(() =>
    props.startDate ? formatedDate(props.startDate) : 'N/A'
);

const formattedEndDate = computed(() =>
    props.endDate ? formatedDate(props.endDate) : 'N/A'
);

const displaySelectedCustomer = computed(() => {
  if (isLoadingCustomerName.value) return "Loading customer...";
  return customerNameDisplay.value || (props.customerId ? `Customer ID: ${props.customerId}` : 'N/A');
});

watch(() => [props.customerId, props.visible], async ([newCustomerId, newVisibleState]) => {
  if (newVisibleState && newCustomerId) {
    isLoadingCustomerName.value = true;
    try {
      await new Promise(resolve => setTimeout(resolve, 300));
      if (newCustomerId === "9eaaecd0-0e16-424b-b0d3-35d8ae6fa599") {
        customerNameDisplay.value = "Thomas Kershaw Ltd.";
      } else if (typeof newCustomerId === 'string') {
        customerNameDisplay.value = `Customer (${newCustomerId.substring(0, 6)}...)`;
      } else {
        customerNameDisplay.value = `Customer ID: ${String(newCustomerId)}`;
      }
    } catch (error) {
      console.error("Failed to fetch customer name:", error);
      customerNameDisplay.value = `Customer ID: ${newCustomerId}`;
    } finally {
      isLoadingCustomerName.value = false;
    }
  } else if (!newVisibleState) {
    customerNameDisplay.value = null;
  }
}, { immediate: true });

const onDownloadReportPdf = () => {
  const reportElement = document.getElementById(PDF_EXPORT_ELEMENT_ID);
  if (!reportElement || !props.reportData) return;
  const filename = `sales-by-customer-${customerNameDisplay.value || props.customerId || 'na'}-${new Date().toISOString().split('T')[0]}.pdf`;
  html2pdf().set({ margin: [10, 5, 10, 5], filename, image: { type: 'jpeg', quality: 0.98 }, html2canvas: { scale: 2, useCORS: true }, jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' } }).from(reportElement).save();
};

const hasReportData = computed(() => !!(props.reportData?.packingLists?.length));

const formatDateForTable = (dateString?: string) => dateString ? new Date(dateString).toLocaleDateString('en-GB') : '-';
const formatCurrency = (value?: number) => typeof value === 'number' ? `£ ${value.toFixed(2)}` : '£ 0.00';
const formatNumberTwoDecimals = (value?: number) => typeof value === 'number' ? value.toFixed(2) : '-';

const tableData = computed(() => {
  if (!props.reportData?.packingLists) return [];

  const flatData: Array<{
    packingListId: string;
    deliveryDate: Date | null;
    qualityReference: string;
    customerPurchaseOrderNumber: string;
    unitPrice: number;
    length: number;
    lengthUnit: string;
    invoiceValueExcludingVat: number;
  }> = [];

  props.reportData.packingLists.forEach(packingList => {
    packingList.orderProducts.forEach(product => {
      flatData.push({
        packingListId: packingList.packingListId,
        deliveryDate: packingList.deliveryDate,
        qualityReference: product.qualityReference,
        customerPurchaseOrderNumber: packingList.customerPurchaseOrderNumber,
        unitPrice: product.unitPrice,
        length: product.length,
        lengthUnit: product.lengthUnit,
        invoiceValueExcludingVat: product.invoiceValueExcludingVat
      });
    });
  });

  return flatData;
});

const totalLength = computed(() => {
  if (!props.reportData?.packingLists) return 0;
  return props.reportData.packingLists.reduce((sum, packingList) => {
    return sum + packingList.orderProducts.reduce((productSum, product) => productSum + product.length, 0);
  }, 0);
});

const grandTotalInvoiceValue = computed(() => {
  if (!props.reportData?.packingLists) return 0;
  return props.reportData.packingLists.reduce((sum, packingList) => sum + packingList.totalInvoiceValueExcludingVat, 0);
});

const getOverallLengthUnit = computed(() => {
  const firstProduct = props.reportData?.packingLists?.[0]?.orderProducts?.[0];
  return firstProduct?.lengthUnit || 'Units';
});

</script>

<template>
  <Dialog :visible="visible" modal :draggable="false" :style="{ width: '85rem', maxWidth: '95vw', minHeight: '90vh'}" :closable="false" @update:visible="onClose" class="report-dialog-custom-header">
    <template #header>
      <div class="flex justify-between items-center w-full">
        <Button link @click="onClose" aria-label="Back to report page" class="flex items-center gap-x-1 p-0 text-gray-600 hover:text-primary-default">
          <Icon icon="material-symbols:arrow-back-rounded" class="text-2xl md:text-3xl group-hover:-translate-x-1 transition-transform" />
          <span class="text-sm md:text-base hidden sm:inline">Back to Report Page</span>
        </Button>
        <div class="flex-grow text-center">
          <h2 class="text-lg md:text-xl font-semibold">Sales by Customer Report</h2>
        </div>
        <div class="flex items-center gap-x-2">
          <Button label="Download as PDF" @click="onDownloadReportPdf" :disabled="!hasReportData || isLoadingCustomerName" class="p-button-danger text-sm md:text-base">
            <template #icon><Icon icon="material-symbols:download-2" class="text-xl ml-3 order-1" /></template>
          </Button>
        </div>
      </div>
    </template>

    <div class="dialog-main-content flex-grow overflow-y-auto">
      <div v-if="(!reportData && visible) || (isLoadingCustomerName && hasReportData)" class="text-center py-10 text-gray-500">
        {{ isLoadingCustomerName ? 'Loading customer details...' : (reportData ? 'Loading report data...' : 'No data to display.') }}
      </div>
      <div v-else-if="hasReportData" :id="PDF_EXPORT_ELEMENT_ID" class="p-2 md:p-4 bg-white">
        <div class="flex justify-between items-start mb-6">
          <div><img src="@/assets/images/logo.png" alt="Company Logo" class="h-12 mb-1" /></div>
          <div class="text-right text-xs text-gray-500 whitespace-pre-line"><p>{{ companyAddress }}</p><p>Tel: {{ companyTel }}</p><p>Fax: {{ companyFax }}</p></div>
        </div>
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6 p-4 bg-gray-50 rounded-md shadow-sm">
          <div><h3 class="text-md font-semibold text-gray-700">Dates</h3><p class="text-sm text-gray-600">Start Date: {{ formattedStartDate }}</p><p class="text-sm text-gray-600">End Date: {{ formattedEndDate }}</p></div>
          <div class="md:text-right">
            <h3 class="text-md font-semibold text-gray-700">Sales by Customer</h3>
            <p class="text-sm text-gray-600">{{ reportData?.businessName }}</p>
          </div>
        </div>

        <div class="overflow-x-auto">
          <DataTable
              :value="tableData"
              responsiveLayout="scroll"
              class="p-datatable-sm sales-by-customer-table min-w-full"
              stripedRows
              :rowHover="true"
              :scrollable="true"
              scrollHeight="60vh"
          >
            <Column field="packingListId" header="Packing List No." sortable :style="{ minWidth: '140px', whiteSpace: 'nowrap' }"></Column>
            <Column field="deliveryDate" header="Delivery Date" sortable :style="{ minWidth: '120px', whiteSpace: 'nowrap' }">
              <template #body="slotProps">{{ formatDateForTable(slotProps.data.deliveryDate) }}</template>
            </Column>
            <Column field="qualityReference" header="Quality Reference" sortable :style="{ minWidth: '150px', whiteSpace: 'nowrap' }"></Column>
            <Column field="customerPurchaseOrderNumber" header="Customer PO No." sortable :style="{ minWidth: '140px', whiteSpace: 'nowrap' }"></Column>
            <Column field="unitPrice" header="Unit Price" sortable :style="{ minWidth: '100px', whiteSpace: 'nowrap' }">
              <template #body="slotProps">{{ formatCurrency(slotProps.data.unitPrice) }}</template>
            </Column>
            <Column field="length" header="Length" sortable :style="{ minWidth: '100px', whiteSpace: 'nowrap' }">
              <template #body="slotProps">
                {{ formatNumberTwoDecimals(slotProps.data.length) }}
                {{ slotProps.data.length && slotProps.data.lengthUnit ? ` ${slotProps.data.lengthUnit}` : '' }}
              </template>
            </Column>
            <Column field="invoiceValueExcludingVat" header="Invoice Value exc VAT" sortable :style="{ minWidth: '160px', whiteSpace: 'nowrap' }">
              <template #body="slotProps">{{ formatCurrency(slotProps.data.invoiceValueExcludingVat) }}</template>
            </Column>

            <ColumnGroup type="footer" v-if="reportData && reportData.packingLists.length > 0">
              <Row>
                <Column footer="Totals by Customer:" :colspan="5" footerStyle="text-align:left; font-weight: bold; color: #1f2937; padding-left: 0.5rem;" />
                <Column :footer="`${formatNumberTwoDecimals(totalLength)} ${totalLength > 0 ? getOverallLengthUnit : ''}`" footerStyle="text-align:right; font-weight: bold; color: #1f2937;" />
                <Column :footer="formatCurrency(grandTotalInvoiceValue)" footerStyle="text-align:right; font-weight: bold; color: #1f2937;" />
              </Row>
            </ColumnGroup>
          </DataTable>
        </div>

      </div>
      <div v-else-if="!hasReportData && visible" class="text-center py-10 text-gray-500">
        No sales data found for the selected customer and date range.
      </div>
    </div>
  </Dialog>
</template>

<style scoped></style>